import * as React from 'react';

import { enumUnit, Unit } from '@rematter/frontend-lib-api-client';
import {
  Button,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogMain,
  DialogOverlay,
  DialogTitle,
  DialogTrigger,
  TextField,
} from '@rematter/ui-web';
import { useSetAtom } from 'jotai';
import { Trash2 } from 'lucide-react';

import {
  $removeReceivedMaterialItem,
  $updateReceivedMaterialItem,
  ReceivedLineItemForm as ReceivedLineItemFormType,
} from '~/modules/trade/_state/loadReconciliation.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { SelectEnum } from '~/shared/components/select-fields/SelectEnum';

interface ReceivedLineItemDialogProps {
  receivedItem: ReceivedLineItemFormType;
  title: string;
  isOpen: boolean;
  onClose: () => void;
  disabled?: boolean;
}

export const ReceivedLineItemDialog = ({
  receivedItem,
  title,
  isOpen,
  onClose,
  disabled = false,
}: ReceivedLineItemDialogProps) => {
  const t = useTranslations();
  const updateReceivedItem = useSetAtom($updateReceivedMaterialItem);
  const removeReceivedItem = useSetAtom($removeReceivedMaterialItem);

  // Local state for dialog form - use pricingUnit, not weight unit
  const [localPricingUnit, setLocalPricingUnit] = React.useState<Unit>(
    receivedItem.pricingUnit ?? enumUnit.Pounds,
  );

  const [localNotes, setLocalNotes] = React.useState(receivedItem.notes ?? '');

  const handleSave = () => {
    // Only update pricing unit and notes - never change weight units
    const updates: Partial<ReceivedLineItemFormType> = {
      notes: localNotes,
      pricingUnit: localPricingUnit,
    };

    updateReceivedItem(receivedItem.id, updates);
    onClose();
  };

  const handleDelete = () => {
    removeReceivedItem(receivedItem.id);
    onClose();
  };

  const handleSelectUnit = (e?: Unit) => setLocalPricingUnit(e as Unit);

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogOverlay className="@ReceivedLineItemDialog">
        <DialogContent
          position="right"
          className="w-[600px]"
          dialogClassName="grid-rows-[auto_1fr_auto]"
        >
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>

          <DialogMain className="flex-1">
            <div className="space-y-6">
              <SelectEnum
                enumValue={enumUnit}
                value={localPricingUnit}
                onChange={handleSelectUnit}
                isDisabled={disabled}
              />

              <div>
                <div className="mb-3 text-sm font-medium text-neutral/90">
                  {t.materialsReconciliation.dialogs.notes}
                </div>
                <TextField
                  value={localNotes}
                  onChange={setLocalNotes}
                  textArea
                  placeholder={
                    t.materialsReconciliation.dialogs.notesPlaceholder
                  }
                  className="w-full"
                  isDisabled={disabled}
                />
              </div>
            </div>
          </DialogMain>

          <DialogFooter>
            {!disabled && (
              <div className="flex w-full flex-row justify-between gap-2">
                <Button
                  variant="outline"
                  color="failure"
                  icon={<Trash2 size={18} />}
                  onPress={handleDelete}
                >
                  {t.materialsReconciliation.dialogs.deleteMaterial}
                </Button>
                <Button onPress={handleSave}>{t.common.save}</Button>
              </div>
            )}
          </DialogFooter>
        </DialogContent>
      </DialogOverlay>
    </DialogTrigger>
  );
};
